import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppMainOverlayHost } from '../../context/AppMainOverlayContext';
import VariablesTable from './VariablesTable';
import './VariablesPanel.css';

const cloneRows = (rows) =>
  (rows || []).map((v) =>
    v && typeof v === 'object'
      ? { ...v, values: Array.isArray(v.values) ? [...v.values] : v.values }
      : v
  );

/**
 * Session body — mounts only while the panel is open so draft state seeds
 * from props once.
 *
 * Cell edits commit on blur / Enter (not per keystroke). Structural edits
 * (add, delete, scope, color) flush immediately. Closing Done/Esc blurs the
 * active cell first so in-progress typing isn't lost.
 */
const VariablesPanelSession = ({
  onClose,
  docVariables = null,
  onDocVariablesChange = null,
  campaign = null,
  onCampaignVariablesChange = null,
  onPatchInheritedVariable = null,
  allSprints = [],
  columnWidths = null,
  onColumnWidthsChange = null,
}) => {
  const [draftDoc, setDraftDoc] = useState(() =>
    onDocVariablesChange ? cloneRows(docVariables) : null
  );
  const [draftCampaignVars, setDraftCampaignVars] = useState(() =>
    onCampaignVariablesChange ? cloneRows(campaign?.variables) : null
  );

  const draftDocRef = useRef(draftDoc);
  const draftCampaignVarsRef = useRef(draftCampaignVars);
  const onDocRef = useRef(onDocVariablesChange);
  const onCampRef = useRef(onCampaignVariablesChange);
  onDocRef.current = onDocVariablesChange;
  onCampRef.current = onCampaignVariablesChange;

  const flush = useCallback(() => {
    // Promote refs → React state so a host re-render can't overwrite values.
    if (draftDocRef.current) {
      setDraftDoc(draftDocRef.current);
      onDocRef.current?.(draftDocRef.current);
    }
    if (draftCampaignVarsRef.current) {
      setDraftCampaignVars(draftCampaignVarsRef.current);
      onCampRef.current?.(draftCampaignVarsRef.current);
    }
  }, []);

  useEffect(() => () => flush(), [flush]);

  // Hard refresh / tab close — push drafts to the host now so its own unload
  // flush (or in-flight save) can still reach the API.
  useEffect(() => {
    const onPageHide = () => {
      const active = document.activeElement;
      if (active?.closest?.('.variables-panel') && typeof active.blur === 'function') {
        active.blur();
      }
      flush();
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onPageHide);
    };
  }, [flush]);

  // Cells commit with silent:false on blur/Enter. Keep silent as a no-op
  // write-through for any legacy callers.
  const handleDocChange = useCallback((vars, { silent = false } = {}) => {
    draftDocRef.current = vars;
    if (silent) return;
    setDraftDoc(vars);
    flush();
  }, [flush]);

  const handleCampaignChange = useCallback((vars, { silent = false } = {}) => {
    draftCampaignVarsRef.current = vars;
    if (silent) return;
    setDraftCampaignVars(vars);
    flush();
  }, [flush]);

  const handleClose = useCallback(() => {
    // Commit the active cell before tearing the panel down
    const active = document.activeElement;
    if (active?.closest?.('.variables-panel') && typeof active.blur === 'function') {
      active.blur();
    }
    flush();
    onClose();
  }, [flush, onClose]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      // Nested confirm (rename / inherited edit) owns Esc first
      if (document.querySelector('.confirm-modal__backdrop')) return;
      // Capture on window + stopImmediate so earlier-registered Esc handlers
      // (doc sheet / mission nav) don't also close the layer underneath.
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      handleClose();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [handleClose]);

  const draftCampaign = useMemo(() => {
    if (!campaign) return null;
    if (!onCampaignVariablesChange) return campaign;
    return { ...campaign, variables: draftCampaignVars || [] };
  }, [campaign, draftCampaignVars, onCampaignVariablesChange]);

  return (
    <>
      <div className="variables-panel__backdrop" onClick={handleClose} aria-hidden />
      <div
        className="variables-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Variables"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="variables-panel__header">
          <h2 className="variables-panel__title">Variables</h2>
          <button className="variables-panel__close" onClick={handleClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="variables-panel__content">
          <VariablesTable
            docVariables={draftDoc}
            onDocVariablesChange={onDocVariablesChange ? handleDocChange : null}
            campaign={draftCampaign}
            onCampaignVariablesChange={onCampaignVariablesChange ? handleCampaignChange : null}
            onPatchInheritedVariable={onPatchInheritedVariable}
            allSprints={allSprints}
            columnWidths={columnWidths}
            onColumnWidthsChange={onColumnWidthsChange}
          />
        </div>

        <div className="variables-panel__footer">
          <button className="variables-panel__done" onClick={handleClose}>
            Done
          </button>
        </div>
      </div>
    </>
  );
};

const VariablesPanel = ({
  isOpen,
  onClose,
  docVariables = null,
  onDocVariablesChange = null,
  campaign = null,
  onCampaignVariablesChange = null,
  onPatchInheritedVariable = null,
  allSprints = [],
  columnWidths = null,
  onColumnWidthsChange = null,
}) => {
  const overlayHost = useAppMainOverlayHost();
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setShouldAnimate(false);
      return undefined;
    }
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() => setShouldAnimate(true))
    );
    return () => cancelAnimationFrame(raf);
  }, [isOpen]);

  if (!isOpen) return null;

  const panel = (
    <div
      className={`variables-panel-root${shouldAnimate ? ' variables-panel-root--open' : ''}`}
    >
      <VariablesPanelSession
        onClose={onClose}
        docVariables={docVariables}
        onDocVariablesChange={onDocVariablesChange}
        campaign={campaign}
        onCampaignVariablesChange={onCampaignVariablesChange}
        onPatchInheritedVariable={onPatchInheritedVariable}
        allSprints={allSprints}
        columnWidths={columnWidths}
        onColumnWidthsChange={onColumnWidthsChange}
      />
    </div>
  );

  return createPortal(panel, overlayHost || document.body);
};

export default VariablesPanel;
